import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import CountUp from "react-countup";
import * as Icons from "lucide-react";
import { ChevronDown, ArrowUpRight, MapPin, Quote } from "lucide-react";
import { Reveal, GMark } from "@/components/Reveal";
import { BENEFITS, STEPS, TESTIMONIALS, FAQS, BADGES, CITIES, IMAGES, PROJECTS } from "@/data/content";

/* ---------------- Counters ---------------- */
export function Counters() {
  const items = [
    { end: 1000, suffix: "+", label: "MWh gerados" },
    { end: 150, suffix: "+", label: "Clientes atendidos" },
    { end: 82, suffix: " t", label: "CO₂ evitado" },
    { end: 6, suffix: " anos", label: "De mercado" },
  ];
  return (
    <section className="bg-primary text-primary-foreground py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
        {items.map((it, i) => (
          <Reveal key={it.label} delay={i * 0.08}>
            <div className="text-center lg:text-left">
              <div className="font-display font-extrabold text-4xl sm:text-6xl tracking-tighter">
                <CountUp end={it.end} duration={2} separator="." enableScrollSpy scrollSpyOnce />{it.suffix}
              </div>
              <p className="mt-2 text-sm text-primary-foreground/80">{it.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Benefits ---------------- */
export function Benefits() {
  return (
    <section id="beneficios" className="relative overflow-hidden py-24 sm:py-32 bg-background">
      <GMark className="h-[380px] -right-16 top-8 rotate-12" opacity={0.05} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-primary mb-4">Por que energia solar</p>
          <h2 className="font-display font-bold text-4xl sm:text-6xl tracking-tighter text-secondary max-w-3xl leading-[0.95]">
            Um investimento que se paga <span className="text-primary italic font-light">e continua rendendo</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map((b, i) => {
            const Icon = Icons[b.icon] || Icons.Sun;
            return (
              <Reveal key={b.title} delay={i * 0.08}>
                <motion.div
                  whileHover={{ y: -6 }}
                  className="group h-full rounded-3xl bg-surface border border-border/60 p-7 transition-shadow hover:shadow-xl hover:shadow-secondary/5"
                  data-testid={`benefit-card-${i}`}
                >
                  <span className="grid place-items-center w-12 h-12 rounded-2xl bg-primary/10 text-primary mb-6 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Icon size={22} />
                  </span>
                  <h3 className="font-display font-bold text-xl text-secondary tracking-tight mb-2">{b.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- How It Works ---------------- */
export function HowItWorks() {
  return (
    <section id="como-funciona" className="py-24 sm:py-32 bg-secondary text-secondary-foreground relative grain overflow-hidden">
      <GMark className="h-[460px] -left-24 bottom-0" opacity={0.07} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <Reveal>
          <p className="overline text-accent mb-4">Como funciona</p>
          <h2 className="font-display font-bold text-4xl sm:text-6xl tracking-tighter max-w-3xl leading-[0.95]">
            Do primeiro contato à economia real
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-2 gap-x-16">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.06}>
              <div className="flex gap-6 py-8 border-t border-white/10">
                <span className="font-display font-extrabold text-5xl sm:text-6xl text-primary/90 tracking-tighter leading-none">{s.n}</span>
                <div>
                  <h3 className="font-display font-bold text-2xl tracking-tight mb-2">{s.title}</h3>
                  <p className="text-secondary-foreground/70 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Solutions ---------------- */
export function Solutions({ onCta }) {
  const blocks = [
    { tag: "Residencial", title: "Para sua casa", img: IMAGES.residential, desc: "Reduza a conta de luz da família, valorize seu imóvel e tenha previsibilidade nos gastos por mais de 25 anos.", points: ["Economia imediata na conta", "Financiamento facilitado", "Valorização do imóvel"] },
    { tag: "Comercial", title: "Para sua empresa", img: IMAGES.commercial, desc: "Corte custos operacionais, ganhe competitividade e comunique sustentabilidade aos seus clientes.", points: ["Redução de custo fixo", "Payback acelerado", "Selo de empresa sustentável"] },
  ];
  return (
    <section id="solucoes" className="relative overflow-hidden py-24 sm:py-32 bg-background">
      <GMark className="h-[420px] -left-20 top-1/4 -rotate-6" opacity={0.05} />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-primary mb-4">Soluções sob medida</p>
          <h2 className="font-display font-bold text-4xl sm:text-6xl tracking-tighter text-secondary max-w-3xl leading-[0.95]">
            Energia solar para cada necessidade
          </h2>
        </Reveal>
        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          {blocks.map((b, i) => (
            <Reveal key={b.tag} delay={i * 0.1}>
              <div className="group rounded-3xl overflow-hidden bg-surface border border-border/60" data-testid={`solution-${b.tag.toLowerCase()}`}>
                <div className="relative h-64 overflow-hidden">
                  <img src={b.img} alt={b.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/70 to-transparent" />
                  <span className="absolute top-5 left-5 overline text-white bg-primary/90 px-3 py-1.5 rounded-full">{b.tag}</span>
                </div>
                <div className="p-8">
                  <h3 className="font-display font-bold text-3xl text-secondary tracking-tight mb-3">{b.title}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-5">{b.desc}</p>
                  <ul className="space-y-2 mb-7">
                    {b.points.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-sm text-secondary">
                        <Icons.Check size={16} className="text-primary" /> {p}
                      </li>
                    ))}
                  </ul>
                  <button onClick={onCta} className="inline-flex items-center gap-2 font-bold text-secondary hover:text-primary transition-colors" data-testid={`solution-cta-${b.tag.toLowerCase()}`}>
                    Solicitar orçamento <ArrowUpRight size={18} />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Projects ---------------- */
export function Projects() {
  return (
    <section id="projetos" className="py-24 sm:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-primary mb-4">Projetos realizados</p>
          <h2 className="font-display font-bold text-4xl sm:text-6xl tracking-tighter text-secondary max-w-3xl leading-[0.95]">
            Instalações que já geram economia
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 auto-rows-[180px] sm:auto-rows-[240px] gap-4">
          {PROJECTS.map((p, i) => (
            <Reveal key={i} delay={i * 0.06} className={p.tall ? "row-span-2" : ""}>
              <div className="group relative w-full h-full rounded-3xl overflow-hidden" data-testid={`project-${i}`}>
                <img src={p.src} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/85 via-transparent to-transparent opacity-70 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-4 left-4 right-4 text-white translate-y-1 group-hover:translate-y-0 transition-transform">
                  <p className="font-display font-bold leading-tight">{p.title}</p>
                  <p className="text-sm text-white/85">{p.location}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Testimonials ---------------- */
export function Testimonials() {
  const [idx, setIdx] = useState(0);
  const t = TESTIMONIALS[idx];
  return (
    <section className="py-24 sm:py-32 bg-secondary text-secondary-foreground relative grain overflow-hidden">
      <GMark className="h-[520px] -right-28 -bottom-16 rotate-12" opacity={0.06} />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 relative">
        <Quote size={56} className="text-primary mb-8" />
        <div className="flex gap-1 mb-5" data-testid="testimonial-stars">
          {[0, 1, 2, 3, 4].map((s) => (
            <Icons.Star key={s} size={20} className="text-accent fill-accent" />
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.blockquote
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="font-display font-medium text-lg sm:text-2xl md:text-3xl leading-snug tracking-tight"
            data-testid="testimonial-text"
          >
            “{t.text}”
          </motion.blockquote>
        </AnimatePresence>
        <div className="mt-10 flex items-center gap-4">
          <img src={t.img} alt={t.name} className="w-14 h-14 rounded-full object-cover border-2 border-primary" />
          <div>
            <p className="font-bold">{t.name}</p>
            <p className="text-sm text-secondary-foreground/60">{t.city}</p>
          </div>
          <div className="ml-auto flex gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-2.5 rounded-full transition-all ${i === idx ? "w-8 bg-primary" : "w-2.5 bg-white/25"}`}
                data-testid={`testimonial-dot-${i}`}
                aria-label={`Depoimento ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
export function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 sm:py-32 bg-background">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-primary mb-4">Perguntas frequentes</p>
          <h2 className="font-display font-bold text-4xl sm:text-6xl tracking-tighter text-secondary leading-[0.95]">Ainda tem dúvidas?</h2>
        </Reveal>
        <div className="mt-12 divide-y divide-border">
          {FAQS.map((f, i) => (
            <div key={i} className="py-2" data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-5 text-left" data-testid={`faq-trigger-${i}`}>
                <span className="font-display font-bold text-lg sm:text-xl text-secondary tracking-tight">{f.q}</span>
                <ChevronDown size={22} className={`shrink-0 text-primary transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {open === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <p className="pb-6 text-muted-foreground leading-relaxed">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Trust marquee + Coverage ---------------- */
export function TrustBadges() {
  return (
    <div className="py-8 bg-surface border-y border-border/60">
      <Marquee speed={40} gradient={false} autoFill>
        {BADGES.map((b) => (
          <span key={b} className="mx-8 inline-flex items-center gap-3 text-secondary/70 font-display font-bold text-lg sm:text-2xl tracking-tight">
            <Icons.BadgeCheck size={22} className="text-primary" /> {b}
          </span>
        ))}
      </Marquee>
    </div>
  );
}

export function Coverage() {
  return (
    <section className="relative overflow-hidden py-24 sm:py-32 bg-background">
      <GMark className="h-[440px] -right-20 bottom-4 rotate-6" opacity={0.05} />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-primary mb-4">Área de cobertura</p>
          <h2 className="font-display font-bold text-4xl sm:text-6xl tracking-tighter text-secondary max-w-3xl leading-[0.95]">
            Atendemos toda a região
          </h2>
          <p className="mt-5 text-muted-foreground max-w-xl">
            Nossa base fica em <strong className="text-secondary">Feira de Santana</strong>, <strong className="text-secondary">Petrolina</strong> e <strong className="text-secondary">Juazeiro</strong> — e atendemos cidades num raio de até 250 km de cada uma delas.
          </p>
        </Reveal>
        <div className="mt-12 flex flex-wrap gap-3">
          {CITIES.map((c, i) => (
            <Reveal key={c} delay={i * 0.03}>
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-5 py-2.5 text-sm font-medium text-secondary hover:border-primary hover:text-primary transition-colors">
                <MapPin size={15} className="text-primary" /> {c}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
